import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LearnerDashboard from './LearnerDashboard';
import AdminDashboard from './AdminDashboard';
import PracticeInstructorStudents from './practice-instructor/PracticeInstructorStudents';
import LoadingSpinner from './common/LoadingSpinner';
import RevisorDashboardPage from './revisor/RevisorDashboardPage';

function Dashboard() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Check if we have a valid token
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('No authentication token found');
        setLoading(false);
        return;
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      // If it's an authentication error, don't show stats
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Authentication failed while fetching dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = () => {
    return user?.roles?.some(role => role.name === 'ADMIN');
  };

  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getWelcomeMessage = () => {
    if (isAdmin() || isBerufsbildner()) {
      return "Welcome to your administrative dashboard.";
    }
    return "Welcome to your Journey dashboard. Here's what's happening with your training progress.";
  };

  const isBerufsbildner = () => {
    return user?.roles?.some(role => role.name === 'BERUFSBILDNER');
  };

  const isLernende = () => {
    return user?.roles?.some(role => role.name === 'LERNENDE');
  };

  const isRevisor = () => {
    return user?.roles?.some(role => role.name === 'REVISOR')
  }

  const isPracticeInstructor = () => {
    return user?.roles?.some(role => role.name === 'PRACTICE_INSTRUCTOR' || role.name === 'PRACTICAL_INSTRUCTOR' || role.name === 'PRAXISBILDNER');
  };

  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  // Show LearnerDashboard for students
  if (isLernende() && !isAdmin() && !isBerufsbildner()) {
    return <LearnerDashboard />;
  }

  // Show PracticeInstructorStudents for Practice Instructors
  if (isPracticeInstructor() && !isAdmin() && !isBerufsbildner()) {
    return <PracticeInstructorStudents />;
  }

  // Show AdminDashboard for Admin/Berufsbildner
  if (isAdmin() || isBerufsbildner()) {
    return <AdminDashboard />;
  }

  if (isRevisor) {
    return <RevisorDashboardPage />
  }


  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="card-brand-elevated p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-4">
                  <h1 className="text-3xl lg:text-4xl font-bold text-grey-dusk">
                    {getTimeOfDay()}, {user?.firstName}!
                  </h1>
                  {isAdmin() && (
                    <span className="px-3 py-1 bg-sunrise-red text-white text-sm font-semibold rounded-full">
                      Administrator
                    </span>
                  )}
                </div>
                <p className="text-lg text-gray-600 max-w-2xl">
                  {getWelcomeMessage()}
                </p>
              </div>
              <div className="mt-6 lg:mt-0 lg:ml-8">
                <div className="w-20 h-20 bg-gradient-to-br from-sunrise-red to-sunrise-600 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-brand">
                  {user?.firstName?.charAt(0)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Access Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Management Hub for Admin/Berufsbildner */}
            {(isAdmin() || isBerufsbildner()) && (
              <div className="bg-ice-white rounded-xl shadow-brand border border-pale-dawn p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-sunrise-red to-sunrise-600 rounded-lg flex items-center justify-center">
                    <i className="fas fa-cogs text-white text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-grey-dusk">Management Hub</h3>
                    <p className="text-sm text-slate-cloud">Centralized management</p>
                  </div>
                </div>
                <p className="text-slate-cloud mb-4">
                  Manage users, schools, classes, and subjects from one convenient location.
                </p>
                <Link 
                  to="/management" 
                  className="inline-flex items-center px-4 py-2 bg-sunrise-red text-white rounded-lg hover:bg-sunrise-600 transition-colors text-decoration-none"
                >
                  Open Hub
                  <i className="fas fa-arrow-right ml-2"></i>
                </Link>
              </div>
            )}

            {/* Apprentice Profiles for Admin/Berufsbildner */}
            {(isAdmin() || isBerufsbildner()) && (
              <div className="bg-ice-white rounded-xl shadow-brand border border-pale-dawn p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-informatik-blue-500 to-informatik-blue-600 rounded-lg flex items-center justify-center">
                    <i className="fas fa-user-graduate text-white text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-grey-dusk">Apprentice Directory</h3>
                    <p className="text-sm text-slate-cloud">View apprentice information</p>
                  </div>
                </div>
                <p className="text-slate-cloud mb-4">
                  View detailed apprentice profiles with personal information, grades, and academic performance.
                </p>
                <Link 
                  to="/students" 
                  className="inline-flex items-center px-4 py-2 bg-informatik-blue-600 text-white rounded-lg hover:bg-informatik-blue-700 transition-colors text-decoration-none"
                >
                  View Profiles
                  <i className="fas fa-arrow-right ml-2"></i>
                </Link>
              </div>
            )}

            {/* Grade Management */}
            <div className="bg-ice-white rounded-xl shadow-brand border border-pale-dawn p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-mediamatik-purple-500 to-mediamatik-purple-600 rounded-lg flex items-center justify-center">
                  <i className="fas fa-graduation-cap text-white text-xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-grey-dusk">Grade Management</h3>
                  <p className="text-sm text-slate-cloud">Manage grades and assessments</p>
                </div>
              </div>
              <p className="text-slate-cloud mb-4">
                {isLernende() 
                  ? "Add and track your exam grades and assessments."
                  : "Manage apprentice grades and track performance across subjects."
                }
              </p>
              <Link 
                to="/grades" 
                className="inline-flex items-center px-4 py-2 bg-mediamatik-purple-600 text-white rounded-lg hover:bg-mediamatik-purple-700 transition-colors text-decoration-none"
              >
                Manage Grades
                <i className="fas fa-arrow-right ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


export default Dashboard;
